import { useCallback, useMemo } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import { Icon } from '@antimatterhq/ui/components/icon';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import { useAtomCallback } from 'jotai/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import {
  dataSourceToDeleteAtom,
  dataSourceUpgradeDialogAtom,
} from '@/atoms/dataSources/dataSourcesAtoms';
import { NestedDropdownMenu } from '@/components/dropdown/NestedDropdown/NestedDropdownMenu';
import { useInstanceId } from '@/contexts/InstanceIdContext';
import { useGetDatasourcePresetVersions } from '@/hooks/content/useGetDatasourcePresetVersions';
import {
  useGetDatasource,
  useOptimisticUpdateGetDataSource,
} from '@/hooks/core/useGetDataSource';
import { useUpdateDataSource } from '@/hooks/core/useUpdateDataSource';
import {
  bumpVersion,
  clearUnpublishedChanges,
  clearUpgradePresetVersion,
  clearUpgradeStatusObject,
  hasUnsavedChanges,
} from '@/lib/datasource';
import { getAvailablePresetVersion } from '@/lib/datasource/upgrade/versions/datasourceUpgradeVersions';
import { DatasourceSummary } from '@/types/api';

interface DataSourceMenuProps {
  dataSource: DatasourceSummary;
  'data-testid'?: string;
}

export const DataSourceMenu = ({
  dataSource,
  'data-testid': testId,
}: DataSourceMenuProps) => {
  const navigate = useNavigate();
  const { instanceId } = useInstanceId();
  const { enablePresetVersioning } = useFlags<FlagSet>();
  const { mutateAsync: updateDataSource } = useUpdateDataSource();
  const optimisticUpdate = useOptimisticUpdateGetDataSource();
  const setUpgradeDialog = useSetAtom(dataSourceUpgradeDialogAtom);

  const { data: datasource } = useGetDatasource({
    name: dataSource?.name || '',
  });

  const { data: presetVersions } = useGetDatasourcePresetVersions({
    name: enablePresetVersioning ? datasource?.spec.usePreset || '' : undefined,
  });

  const { version: latestPresetVersion, state: upgradeState } = useMemo(() => {
    if (!enablePresetVersioning) {
      return { version: undefined, state: 'up-to-date' };
    }
    return getAvailablePresetVersion(datasource, presetVersions);
  }, [datasource, presetVersions, enablePresetVersioning]);

  const onEdit = useCallback(() => {
    navigate(`/configure/data-sources/edit/${dataSource.name}/getting-started`);
  }, [navigate, dataSource.name]);

  const onDelete = useAtomCallback(
    useCallback(
      (_get, set) => {
        set(dataSourceToDeleteAtom, dataSource);
      },
      [dataSource],
    ),
  );

  const onViewJob = useCallback(() => {
    if (datasource?.status?.jobId) {
      window.open(
        `https://${instanceId}/jobs/${datasource.status.jobId}`,
        '_blank',
      );
    }
  }, [datasource?.status?.jobId, instanceId]);

  const handleUpgradeDataSource = useCallback(() => {
    if (!datasource || upgradeState === 'up-to-date') {
      throw new Error(
        `Source: "${datasource?.metadata?.name}" is not available or upgrade version is already up to date`,
      );
    }
    setUpgradeDialog({
      dataSourceName: dataSource.name || '',
      upgradePresetVersion: latestPresetVersion?.toString() || null,
    });
  }, [
    datasource,
    upgradeState,
    setUpgradeDialog,
    dataSource.name,
    latestPresetVersion,
  ]);

  const onClearUnpublishedChanges = useCallback(async () => {
    if (!datasource) {
      return;
    }
    let newSource = clearUnpublishedChanges(datasource);
    newSource = clearUpgradePresetVersion(newSource);
    newSource = clearUpgradeStatusObject(newSource);
    newSource = bumpVersion(newSource);
    optimisticUpdate({
      data: newSource,
      name: datasource.metadata?.name || '',
    });
    updateDataSource(
      { data: newSource, name: newSource.metadata?.name || '' },
      {
        toasts: {
          error: 'Failed to clear unpublished changes',
        },
        onError: async () => {
          optimisticUpdate({
            data: datasource,
            name: datasource.metadata?.name || '',
          });
        },
      },
    );
  }, [datasource, updateDataSource, optimisticUpdate]);

  const hasUnpublishedChanges = useMemo(() => {
    if (!datasource) {
      return false;
    }
    return hasUnsavedChanges(datasource);
  }, [datasource]);

  const menuItems = useMemo(() => {
    return [
      {
        label: 'Delete',
        onClick: () => onDelete(),
      },
      ...(upgradeState !== 'in-progress'
        ? [
            {
              label: 'Edit',
              onClick: () => onEdit(),
            },
          ]
        : []),
      ...(datasource?.status?.jobId
        ? [
            {
              label: 'View job',
              onClick: onViewJob,
            },
          ]
        : []),
      ...(hasUnpublishedChanges
        ? [
            {
              label:
                upgradeState === 'in-progress'
                  ? 'Abort upgrade'
                  : 'Discard changes',
              onClick: onClearUnpublishedChanges,
            },
          ]
        : []),
      ...(upgradeState === 'in-progress'
        ? [
            {
              label: 'Continue preset upgrade',
              onClick: handleUpgradeDataSource,
            },
          ]
        : []),
    ];
  }, [
    datasource?.status?.jobId,
    handleUpgradeDataSource,
    hasUnpublishedChanges,
    onClearUnpublishedChanges,
    onDelete,
    onEdit,
    onViewJob,
    upgradeState,
  ]);

  return (
    <>
      <NestedDropdownMenu
        side="right"
        trigger={
          <Button className="h-8 w-8" data-testid={testId}>
            <Icon icon="lucide:ellipsis-vertical" />
          </Button>
        }
        menuItems={menuItems}
        data-testid={testId}
      />
    </>
  );
};
